import { useState } from "react";
import { Plus, Search } from "lucide-react";
import { useCustomerGroups } from "@/features/pessoas/hooks/useCustomerGroups";
import { CustomerGroupsTable } from "@/features/pessoas/components/CustomerGroupsTable";
import { CustomerGroupDialog } from "@/features/pessoas/components/CustomerGroupDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { CustomerGroup } from "@/features/pessoas/types";
import type { CustomerGroupFormData } from "@/features/pessoas/schemas/customerGroupSchema";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

const CustomerGroups = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CustomerGroup | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    groups,
    isLoading,
    createGroup,
    updateGroup,
    deleteGroup,
    isCreating,
    isUpdating,
  } = useCustomerGroups();

  const filteredGroups = groups?.filter((group) =>
    group.descricao.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const paginacao = usePaginacaoLocal(filteredGroups);

  const handleOpenDialog = (group?: CustomerGroup) => {
    setEditingGroup(group || null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingGroup(null);
  };

  const handleSubmit = (data: CustomerGroupFormData) => {
    if (editingGroup) {
      updateGroup(
        { id: editingGroup.id, data: { descricao: data.descricao, isInativo: data.isInativo ?? false } },
        { onSuccess: () => handleCloseDialog() }
      );
    } else {
      createGroup(
        { descricao: data.descricao, isInativo: data.isInativo ?? false },
        { onSuccess: () => handleCloseDialog() }
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Grupos de Clientes</h1>
          <p className="text-muted-foreground">Gerencie os grupos de clientes</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Grupo
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar grupos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <CustomerGroupsTable
        groups={paginacao.itensPaginados}
        onEdit={handleOpenDialog}
        onDelete={deleteGroup}
      />

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <CustomerGroupDialog
        open={dialogOpen}
        onOpenChange={handleCloseDialog}
        onSubmit={handleSubmit}
        editingGroup={editingGroup}
        isLoading={isCreating || isUpdating}
      />
    </div>
  );
};

export default CustomerGroups;
